import { rem } from "@certn/cascadia";
import {
  CascadiaColor,
  CascadiaSpacing,
  CascadiaStandardIcon,
  CascadiaTypographyStyle,
  sizes,
  textStyles,
} from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import React, { PropsWithChildren } from "react";

import { Icon } from "../../foundations/icons/Icon/Icon";
import { Text } from "../../foundations/typography/Text";

export type size = "primary" | "secondary";
export interface ListProps {
  type: "unordered" | "ordered";
  size?: size;
  testID?: string;
  marginTop?: CascadiaSpacing;
}
export interface ListItemProps {
  size?: size;
  testID?: string;
  style?: CascadiaTypographyStyle;
  icon?: CascadiaStandardIcon;
  iconColor?: CascadiaColor;
  removeListStyleType?: boolean;
}

export function ListItem({
  children,
  testID = "list-item",
  size = "primary",
  icon,
  iconColor = "icon-accent-tofino",
  removeListStyleType,
}: PropsWithChildren<ListItemProps>) {
  const style = size === "primary" ? "form/list" : "form/list-secondary";
  const values =
    size === "primary"
      ? textStyles["form/list"]
      : textStyles["form/list-secondary"];

  return (
    <li
      className={css({
        "::marker": {
          color: "var(--text-base)",
          fontFamily: "var(--font-family-default)",
          fontSize: rem(sizes[values.size]),
          lineHeight: values.lineHeight,
          fontWeight: values.weight,
        },
        listStyleType: icon || removeListStyleType ? "none" : undefined,
        marginLeft: icon ? "calc(var(--space-5) * -1)" : undefined,
      })}
      data-testid={testID}
    >
      <div
        className={
          icon
            ? css({ columnGap: "var(--space-2)", display: "flex" })
            : undefined
        }
      >
        {icon && <Icon size="space-6" color={iconColor} name={icon} />}
        <Text style={style} color="text-base">
          {children}
        </Text>
      </div>
    </li>
  );
}

export function List({
  type,
  size = "primary",
  testID = "list",
  children,
  marginTop,
}: PropsWithChildren<ListProps>) {
  const ListComponent = type === "unordered" ? "ul" : "ol";
  const childProps: Partial<ListItemProps> = { size };

  return (
    <ListComponent
      data-testid={testID}
      className={css({
        margin: 0,
        padding: 0,
        marginTop: marginTop ? rem(marginTop) : 0,
        paddingInlineStart: "var(--space-5)",
      })}
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, childProps);
        }

        return child;
      })}
    </ListComponent>
  );
}
